import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const COURSE_URL = 'https://clearmindclub931.systeme.io/clearmindblueprint';

const modules = [
  {
    number: '01',
    title: 'The Foundation',
    subtitle: 'Understanding Cognitive Wellness',
    lessons: ['What is Brain Fog Really?', 'The Science of Mental Clarity', 'Your Cognitive Baseline Assessment', 'Setting Up Your Wellness Environment'],
  },
  {
    number: '02',
    title: 'Daily Routines',
    subtitle: 'Building Habits That Stick',
    lessons: ['The Morning Clarity Protocol', 'Midday Energy Management', 'Evening Wind-Down Rituals', 'Weekend Deep Reset Practices'],
  },
  {
    number: '03',
    title: 'Nutrition & Nootropics',
    subtitle: 'Fuel Your Brain Right',
    lessons: ['Brain-Boosting Foods & Meal Plans', 'Understanding Nootropic Stacks', 'Supplement Timing & Cycling', 'Hydration & Cognitive Performance'],
  },
  {
    number: '04',
    title: 'Mind Training',
    subtitle: 'Sharpen Your Mental Edge',
    lessons: ['Focus & Concentration Drills', 'Memory Enhancement Techniques', 'Creative Thinking Exercises', 'Stress Resilience Training'],
  },
  {
    number: '05',
    title: 'Productivity Systems',
    subtitle: 'Work Smarter, Not Harder',
    lessons: ['Deep Work Frameworks', 'Digital Minimalism Setup', 'Task Batching & Time Blocking', 'Energy-Based Scheduling'],
  },
  {
    number: '06',
    title: 'Lifetime Integration',
    subtitle: 'Making It Sustainable',
    lessons: ['Building Your Personal System', 'Tracking & Measuring Progress', 'Adapting to Life Changes', 'Community & Accountability'],
  },
];

const benefits = [
  { icon: 'ri-lightbulb-flash-line', title: 'Clear Brain Fog', description: 'Proven techniques to cut through mental haze and think with razor-sharp clarity, even on your busiest days.' },
  { icon: 'ri-focus-3-line', title: 'Laser Focus', description: 'Train your attention span like a muscle. Go from scattered to dialled-in with structured focus protocols.' },
  { icon: 'ri-emotion-happy-line', title: 'Better Mood', description: 'The cognitive-nutritional connection that stabilises your mood and keeps you even-keeled throughout the day.' },
  { icon: 'ri-rocket-line', title: 'Peak Productivity', description: 'Stop working harder. Learn energy-based scheduling and deep work frameworks that multiply your output.' },
  { icon: 'ri-brain-line', title: 'Memory Mastery', description: 'Practical memory techniques backed by neuroscience — not gimmicks, just what actually works.' },
  { icon: 'ri-heart-pulse-line', title: 'Sustainable Wellness', description: 'Build a personal system that evolves with you. No burnout, no falling off the wagon, just steady progress.' },
];

export default function CoursePage() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main ref={sectionRef}>

        {/* Hero Section */}
        <section className="relative overflow-hidden min-h-[550px] md:min-h-[650px] flex items-center justify-center">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20soft%20warm%20gradients%20in%20muted%20lime%20green%20and%20cream%20tones%2C%20minimalist%20design%2C%20calm%20atmospheric%20texture%2C%20clean%20modern%20aesthetic%2C%20subtle%20overlapping%20circles%20and%20organic%20shapes%2C%20peaceful%20meditative%20vibe%2C%20high%20resolution%20abstract%20background&width=1600&height=900&seq=course-hero-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/30 to-background-50/70" />

          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-20 sm:py-32 text-center">
            <div className="reveal-on-scroll inline-flex items-center gap-2 bg-primary-500/90 text-background-50 text-sm font-label font-medium px-4 py-1.5 rounded-full mb-6">
              <i className="ri-graduation-cap-line" />
              New Course
            </div>

            <h1 className="reveal-on-scroll font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-3xl mx-auto leading-tight text-foreground-950">
              ClearMind
              <br />
              <span className="text-primary-500">Blueprint</span>
            </h1>

            <p className="reveal-on-scroll text-lg sm:text-xl max-w-2xl mx-auto mb-4 leading-relaxed text-foreground-700 font-body">
              A step-by-step system to clear brain fog, sharpen your focus, and build cognitive wellness habits that actually stick.
            </p>

            <p className="reveal-on-scroll text-sm text-foreground-500 mb-6 font-body max-w-lg mx-auto">
              Self-paced course with 6 modules, 24 lessons, lifetime access, and community support.
            </p>

            <div className="reveal-on-scroll flex items-center justify-center gap-3 mb-10">
              <span className="font-heading text-4xl sm:text-5xl font-bold text-primary-500">£297</span>
              <span className="text-sm text-foreground-500 font-body">one-time payment · lifetime access</span>
            </div>

            <div className="reveal-on-scroll flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={COURSE_URL}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 bg-primary-500 text-background-50 hover:bg-primary-600 h-12 rounded-lg w-full sm:w-auto text-base px-8"
              >
                Enroll Now
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="#what-youll-learn"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 border border-background-300 bg-transparent hover:bg-background-100 h-12 rounded-lg w-full sm:w-auto text-base px-8 text-foreground-800"
              >
                See What's Inside
                <i className="ri-arrow-down-line" />
              </a>
            </div>
          </div>
        </section>

        {/* What You'll Learn */}
        <section id="what-youll-learn" className="w-full px-4 sm:px-6 lg:px-8 py-20 md:py-28 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="reveal-on-scroll text-center mb-14">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-primary-500">
                What You'll Learn
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto font-body leading-relaxed">
                Every module builds on the last, taking you from foggy and scattered to clear and in control.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, i) => (
                <div
                  key={benefit.title}
                  className="reveal-on-scroll bg-background-100 border border-background-200/70 rounded-2xl p-6 transition-all duration-300 hover:border-accent-300/60"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                    <i className={`${benefit.icon} text-accent-600 text-lg`} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2 text-foreground-950">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-foreground-600 font-body leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Course Curriculum */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-20 md:py-28 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="reveal-on-scroll text-center mb-14">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-primary-500">
                Course Curriculum
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto font-body leading-relaxed">
                6 structured modules with 24 in-depth lessons. Work through at your own pace with lifetime access.
              </p>
            </div>

            <div className="space-y-4">
              {modules.map((mod, i) => (
                <details
                  key={mod.number}
                  className="reveal-on-scroll group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden transition-all duration-300"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <summary className="flex items-center gap-5 p-5 sm:p-6 cursor-pointer list-none hover:bg-background-100/80 transition-colors">
                    <span className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 font-heading font-bold text-lg sm:text-xl shrink-0">
                      {mod.number}
                    </span>
                    <div className="flex-1 min-w-0 text-left">
                      <h3 className="font-heading text-lg sm:text-xl font-semibold text-foreground-950">
                        {mod.title}
                      </h3>
                      <p className="text-sm text-foreground-500 font-body mt-0.5">
                        {mod.subtitle}
                      </p>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 group-open:bg-accent-100 transition-colors shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-600 group-open:rotate-180 transition-transform duration-300" />
                    </div>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1">
                    <ul className="space-y-2 pl-16 sm:pl-[4.25rem]">
                      {mod.lessons.map((lesson) => (
                        <li key={lesson} className="flex items-center gap-3 text-sm text-foreground-700 font-body">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent-400 shrink-0" />
                          {lesson}
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-20 md:py-28 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="reveal-on-scroll text-center mb-14">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-primary-500">
                Who Is This For?
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto font-body leading-relaxed">
                The Blueprint works whether you're just starting your wellness journey or you've tried everything and nothing stuck.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="reveal-on-scroll bg-background-100 border border-background-200/70 rounded-2xl p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 mb-5">
                  <i className="ri-check-double-line text-accent-600 text-lg" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-4 text-foreground-950">
                  This course is perfect if you...
                </h3>
                <ul className="space-y-3">
                  {[
                    'Wake up feeling foggy and struggle to get going',
                    'Find yourself distracted and unable to focus deeply',
                    'Have tried random tips and supplements with no real results',
                    'Want a structured system instead of scattered advice',
                    'Are ready to invest in your long-term cognitive health',
                    'Prefer self-paced learning with practical action steps',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-foreground-700 font-body">
                      <i className="ri-check-line text-accent-500 text-base mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="reveal-on-scroll bg-background-100 border border-background-200/70 rounded-2xl p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-200/70 mb-5">
                  <i className="ri-close-line text-foreground-500 text-lg" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-4 text-foreground-950">
                  This might not be for you if...
                </h3>
                <ul className="space-y-3">
                  {[
                    'You are looking for a quick magic pill solution',
                    'You are not willing to put in consistent daily effort',
                    'You need urgent medical or psychiatric treatment',
                    'You want one-on-one coaching (this is a self-paced course)',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-foreground-600 font-body">
                      <i className="ri-close-line text-foreground-400 text-base mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-20 md:py-28 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="reveal-on-scroll bg-gradient-to-br from-primary-100/60 to-accent-100/60 border border-background-200/70 rounded-3xl p-10 sm:p-16 text-center">
              <div className="inline-flex items-center gap-2 bg-accent-500 text-foreground-950 text-sm font-label font-medium px-4 py-1.5 rounded-full mb-6">
                <i className="ri-star-line" />
                Start Your Blueprint Today
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-primary-500">
                Ready to Clear the Fog?
              </h2>
              <p className="text-foreground-600 max-w-lg mx-auto mb-8 font-body leading-relaxed">
                Join the ClearMind Blueprint and get lifetime access to all 6 modules, 24 lessons, and our supportive community. Your clearer mind is waiting.
              </p>
              <a
                href={COURSE_URL}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 bg-primary-500 text-background-50 hover:bg-primary-600 h-12 rounded-lg text-base px-8"
              >
                Enroll in the Blueprint
                <i className="ri-arrow-right-line" />
              </a>
              <p className="text-xs text-foreground-500 mt-4 font-label">
                £297 one-time &middot; Lifetime access &middot; Self-paced &middot; 6 modules included
              </p>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}